import React, { useState } from 'react';
import { OrderModel } from '@app/interfaces/order';
import { Badge, Collapse, Descriptions, Tag } from 'antd';
import { formatDateTime, formatMoney, getNumberInString } from '@app/utils/utils';
import CustomerType from '@app/components/partner/customer/components/CustomerType';
import { EditOutlined, EyeOutlined, FieldTimeOutlined, FileImageOutlined, FileTextOutlined, SafetyOutlined, UserOutlined } from '@ant-design/icons';
import OrderStatus from '../components/OrderStatus';
import { TbTruckDelivery } from 'react-icons/tb';
import { IoMdTime } from 'react-icons/io';
import { IoCheckmarkDoneCircleSharp } from 'react-icons/io5';
import GoogleMapView from '@app/components/common/GoogleMapView';
import OverviewModal from '@app/components/partner/customer/overview/OverviewModal';
import { getCustomer } from '@app/store/slices/customerSlice';
import OrderTypeRender from '../components/OrderTypeRender';
import OrderInfo from './OrderInfo';
import OrderConfirmedByCustomerImages from './OrderConfirmedByCustomerImages';
import OrderCompletedImages from './OrderCompletedImages';
import OrderDeliveredImages from './OrderDeliveredImages';
import { useAppDispatch } from '@app/hooks/reduxHooks';
import { usePermissions } from '@app/hooks/usePermissions';
import { MdOutlinePayments } from 'react-icons/md';
import { Link } from 'react-router-dom';
import { FcCancel } from 'react-icons/fc';
import RemarkEditModal from './RemarkEditModal';
import UserAvatar from '@app/components/common/UserAvatar/UserAvatar';
import OrderQrCode from '../delivery/OrderQrCode';

interface OrderInfoProps {
  record: OrderModel;
}

const LABEL_STYLE = { fontWeight: 500, width: 160, background: '#fafafa', color: '#595959' };
const DESC_COLS = { xs: 1, sm: 2, md: 2 };

/** Header panel Collapse: icon + tiêu đề cùng tông màu, badge đếm số lượng. */
const PanelHeader: React.FC<{
  icon: React.ReactNode;
  title: string;
  iconColorClass: string;
  titleColorClass: string;
  count: number;
  badgeColor: string;
}> = ({ icon, title, iconColorClass, titleColorClass, count, badgeColor }) => (
  <span className="tw-inline-flex tw-items-center tw-gap-2 tw-text-[13px] tw-font-medium">
    <span className={`tw-text-[15px] ${iconColorClass}`}>{icon}</span>
    <span className={`tw-tracking-[0.01em] ${titleColorClass}`}>{title}</span>
    <Badge count={count} style={{ backgroundColor: badgeColor, marginLeft: 8 }} overflowCount={99} />
  </span>
);

const Overview: React.FC<OrderInfoProps> = ({ record }) => {
  // Người giao thật (shipper chuyến / shipper đơn) — khác tài khoản bấm Hoàn thành (delivered_by).
  const deliveryShipper = record.delivery_shipper_name || record.shipper_by;
  const [customerRecord, setCustomerRecord] = useState<any | null>(null);
  const [showCustomerOverviewModal, setShowCustomerOverviewModal] = useState(false);
  const [showRemarkModal, setShowRemarkModal] = useState(false);
  const [currentRemark, setCurrentRemark] = useState(record.remark);
  const dispatch = useAppDispatch();
  // Xem chi tiết khách hàng gọi /api/customers/:id (cần customers:show).
  const { can } = usePermissions();

  /** Số ảnh theo từng loại — hiển thị badge trên header panel cho đồng bộ với panel "Thông tin đơn hàng". */
  const countImages = (assetType: string) =>
    (record.images ?? []).filter((image) => image.asset_type === assetType).length;

  const handleViewCustomer = async () => {
    try {
      const response = await dispatch(getCustomer(Number(record.customer_id))).unwrap();
      if (response?.data) {
        setCustomerRecord(response.data.attributes);
        setShowCustomerOverviewModal(true);
      }
    } catch (error) {
      console.error('Error fetching customer:', error);
    }
  };

  return (
    <>
      {/* Status bar */}
      <div className="tw-flex tw-flex-wrap tw-items-center tw-justify-between tw-gap-2 tw-border-0 tw-border-b tw-border-solid tw-border-surface-container-low tw-px-4 tw-py-3">
        <div className="tw-flex tw-items-center tw-gap-2">
          <span className="tw-font-medium tw-text-on-surface-variant">Trạng thái đơn hàng:</span>
          <OrderStatus status={record?.order_status} />
        </div>
        {/* QR mở trang xử lý nhanh trên điện thoại (bấm để phóng to). */}
        <OrderQrCode orderCode={record.order_code} size={56} />
      </div>

      <div className="tw-p-4">
        {/* ── Thông tin chung ─────────────────────────────────────── */}
        <Descriptions
          bordered
          size="small"
          column={DESC_COLS}
          labelStyle={LABEL_STYLE}
          className="tw-mb-4"
        >
          <Descriptions.Item label="Mã hoá đơn">
            <span className="text-primary font-bold-weight">{record.order_code}</span>
          </Descriptions.Item>
          <Descriptions.Item label="Ngày hoá đơn">
            <span className="text-primary">{formatDateTime(record.trans_date)}</span>
          </Descriptions.Item>

          <Descriptions.Item label="Tên khách hàng">
            <span className="text-primary font-bold-weight">
              {record.customer.name}{' '}
              {can('customers', 'show') && (
                <EyeOutlined
                  title="Chi tiết khách hàng"
                  onClick={handleViewCustomer}
                  className="pointer tw-text-primary"
                />
              )}
            </span>
          </Descriptions.Item>
          <Descriptions.Item label="Kênh bán">
            {record.channel?.name
              ? <Tag color="geekblue">{record.channel.name}</Tag>
              : <span className="tw-text-outline">—</span>}
          </Descriptions.Item>

          <Descriptions.Item label="Loại khách hàng">
            <CustomerType value={record.customer.customer_type} />
          </Descriptions.Item>
          <Descriptions.Item label="Nhóm khách hàng">
            <Tag color="blue">{record.customer?.group?.name || '—'}</Tag>
          </Descriptions.Item>

          <Descriptions.Item label="Nhân viên bán">
            <span className="text-primary">{record.seller_by || '—'}</span>
            {record.co_seller_by &&
              record.co_seller_by.split(', ').map((name, i) => (
                <Tag key={i} color="cyan" className="tw-ml-1">{name}</Tag>
              ))}
          </Descriptions.Item>
          <Descriptions.Item label="Nhân viên thiết kế hoa">
            {record.designer_by
              ? record.designer_by.split(', ').map((name, i) => (
                  <Tag key={i} color="purple">{name}</Tag>
                ))
              : <span className="tw-text-outline">Khác</span>}
          </Descriptions.Item>

          <Descriptions.Item label="Nhân viên giao hàng">
            <span className="text-primary">{record.shipper_by || 'Khác'}</span>
          </Descriptions.Item>
          <Descriptions.Item label={<><IoMdTime /> Thời gian giao hàng</>}>
            <span className="text-primary font-bold-weight">{formatDateTime(record.delivery_date)}</span>
          </Descriptions.Item>
        </Descriptions>

        {/* ── Tài chính & trạng thái ──────────────────────────────── */}
        <Descriptions
          bordered
          size="small"
          column={DESC_COLS}
          labelStyle={LABEL_STYLE}
          className="tw-mb-4"
        >
          <Descriptions.Item label="Giá trị đơn hàng">
            <span className="text-primary font-bold-weight">{formatMoney(record.total_amount)}</span>
          </Descriptions.Item>
          <Descriptions.Item label="Công nợ khách hàng">
            <span className="text-danger font-bold-weight">{formatMoney(record.debit_amount)}</span>
          </Descriptions.Item>

          <Descriptions.Item label="Loại thanh toán">
            {record.debit_amount === 0
              ? <Tag color="#f50"><MdOutlinePayments /> {record.payment_type_name}</Tag>
              : <Tag color="warning">Chưa thanh toán</Tag>}
          </Descriptions.Item>
          <Descriptions.Item label="Loại">
            <OrderTypeRender status={record.order_type} />
          </Descriptions.Item>

          <Descriptions.Item label="Số KM giao hàng">
            <Tag color="#f50">{record.shipper_number_of_km || 0} km</Tag>
          </Descriptions.Item>
          <Descriptions.Item label="Ghi chú">
            <span className="text-danger">{currentRemark}</span>
            {record.permission?.allow_update_remark && (
              <EditOutlined
                onClick={() => setShowRemarkModal(true)}
                className="pointer ms-2 tw-text-primary"
                title="Cập nhật ghi chú"
              />
            )}
          </Descriptions.Item>

          {record.group_code && (
            <Descriptions.Item label="Gộp đơn" span={2}>
              {record.group_code.map((item) => (
                <Tag key={item}>
                  <Link to={`/orders/${getNumberInString(item)}`} target="_blank">{item}</Link>
                </Tag>
              ))}
            </Descriptions.Item>
          )}

          {record.reason && (
            <Descriptions.Item label={<><FcCancel /> Lý do huỷ</>} span={2}>
              <span className="text-danger">{record.reason}</span>
            </Descriptions.Item>
          )}

          <Descriptions.Item label={<><UserOutlined /> Người tạo</>}>
            <UserAvatar name={record.created_by} avatarUrl={record.created_by_avatar_url} />
          </Descriptions.Item>
          <Descriptions.Item label={<><FieldTimeOutlined /> Ngày tạo</>}>
            <span className="text-primary">{record.created_date}</span>
          </Descriptions.Item>
        </Descriptions>

        {/* ── Địa chỉ giao hàng ──────────────────────────────────── */}
        <Descriptions
          bordered
          size="small"
          column={1}
          labelStyle={LABEL_STYLE}
          className="tw-mb-4"
        >
          <Descriptions.Item label={<><TbTruckDelivery className="text-warning" /> Địa chỉ giao hàng</>}>
            <GoogleMapView address={record.delivery_address} />
          </Descriptions.Item>
        </Descriptions>

        {/* ── Bằng chứng giao hàng (lưu khi Hoàn thành) ─────────────── */}
        {record.delivered_at && (
          <Descriptions bordered size="small" column={{ xs: 1, md: 2 }} labelStyle={LABEL_STYLE} className="tw-mb-4">
            <Descriptions.Item label="Đã giao lúc">{record.delivered_at}</Descriptions.Item>
            <Descriptions.Item label="Người giao">{deliveryShipper || '—'}</Descriptions.Item>
            <Descriptions.Item label="Người nhận">{record.recipient_name || '—'}</Descriptions.Item>
            <Descriptions.Item label="SĐT người nhận">{record.recipient_phone || '—'}</Descriptions.Item>
            {/* Sales/quản lý bấm Hoàn thành hộ shipper → hiện rõ ai xác nhận. */}
            {record.delivered_by && record.delivered_by !== deliveryShipper && (
              <Descriptions.Item label="Xác nhận bởi">{record.delivered_by}</Descriptions.Item>
            )}
          </Descriptions>
        )}
      </div>

      {/* ── Collapse panels ─────────────────────────────────────── */}
      <div className="tw-px-4 tw-pb-4">
        <Collapse defaultActiveKey="1">
          <Collapse.Panel
            key="1"
            header={
              <PanelHeader
                icon={<FileTextOutlined />}
                title="Thông tin đơn hàng"
                iconColorClass="tw-text-primary"
                titleColorClass="tw-text-primary"
                count={record.items?.length ?? 0}
                badgeColor="var(--primary-color)"
              />
            }
          >
            <OrderInfo record={record} />
          </Collapse.Panel>

          <Collapse.Panel
            key="2"
            header={
              <PanelHeader
                icon={<SafetyOutlined />}
                title="Xác nhận bởi khách hàng"
                iconColorClass="tw-text-[#722ed1]"
                titleColorClass="tw-text-[#722ed1]"
                count={countImages('confirmed')}
                badgeColor="#722ed1"
              />
            }
          >
            <OrderConfirmedByCustomerImages record={record} />
          </Collapse.Panel>

          <Collapse.Panel
            key="3"
            header={
              <PanelHeader
                icon={<IoCheckmarkDoneCircleSharp />}
                title="Hình ảnh thành phẩm"
                iconColorClass="tw-text-success-teal"
                titleColorClass="tw-text-success-teal"
                count={countImages('completed')}
                badgeColor="var(--success-color)"
              />
            }
          >
            <OrderCompletedImages record={record} />
          </Collapse.Panel>

          <Collapse.Panel
            key="4"
            header={
              <PanelHeader
                icon={<FileImageOutlined />}
                title="Hình ảnh giao hàng"
                iconColorClass="tw-text-warning"
                titleColorClass="tw-text-warning"
                count={countImages('delivered')}
                badgeColor="var(--warning-color)"
              />
            }
          >
            <OrderDeliveredImages record={record} />
          </Collapse.Panel>
        </Collapse>
      </div>

      {showCustomerOverviewModal && (
        <OverviewModal
          open={showCustomerOverviewModal}
          record={customerRecord}
          onClose={() => setShowCustomerOverviewModal(false)}
          source="order"
        />
      )}

      <RemarkEditModal
        orderId={Number(record.id)}
        initialRemark={currentRemark || ''}
        open={showRemarkModal}
        onClose={() => setShowRemarkModal(false)}
        onSuccess={(newRemark) => setCurrentRemark(newRemark)}
      />
    </>
  );
};

export default Overview;
